import { MarketNotReady } from './MarketScopeTabs';
import { useMarketReady, useMarketScope } from './marketScope';
import { Fragment, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge, Card, EmptyState, ErrorState, Pager, Spinner, Table, Td, Th, cx } from '@/components/ui';
import {
  changeColorClass,
  formatDate,
  formatDateTime,
  formatPercent,
  toThousands,
} from '@/lib/format';
import { usePaperChartColors } from './paperChartColors';
import { PaperComparisonPanel } from './PaperComparisonPanel';
import { TradingNav } from './TradingNav';
import {
  daysToExpiry,
  emptyReason,
  enteredAgainstCondition,
  isPreMarketBuy,
  type PaperTab,
} from './paperDisplay';
import { AccountStatsCard } from './AccountStatsCard';
import { daysHeldLabel } from './accountStats';
import { HelpButton } from './HelpButton';
import { PROFIT_HELP } from './profitHelp';
import { BandPanel } from './BandPanel';
import { ProfileHelpModal } from './ProfileHelpModal';
import { PaperRoundsPanel } from './PaperRoundsPanel';
import { StockLink } from './StockLink';
import { kstDateString } from './kstDate';
import type { PaperOverview, PaperProfile } from './paperTypes';
import { BandPriceFormatContext, bandPriceFormat, useBandPriceFormat } from './bandCurrency';
import { insertAfter, withoutResting } from './paperDisplay';
import { AI_REVIEW_MARK, AI_REVIEW_MARK_LABEL, isAiReviewAccount } from './aiReviewMark';
import { REVIEW_PAUSED_NOTE } from './reviewPause';
import { SwingPanel } from './SwingPanel';
import type { SwingAccountKey } from './swingTypes';
import { usePaperComparison, usePaperOverview, useRestingProfiles, useSwingAccounts } from './usePaper';

/**
 * AI 모의투자. (18단계, 문서 8.3절)
 *
 * AI 가 자기 추천대로 실제로 굴렸으면 어땠는지를 숫자로 남긴다.
 * 프로필별 계좌 하나씩. 시드는 계좌마다 다를 수 있어(관리자 초기화에서 정한다)
 * 화면에 숫자를 박지 않고 응답의 값을 그대로 쓴다. (2026-08-30)
 *
 * 🔴 토스의 모의투자 기능이 아니다
 *   주문 API 는 어떤 경로로도 부르지 않는다. 전부 우리 DB 안의 계산이다.
 *   이 화면에도 사고파는 버튼이 없다. 배치가 굴린다.
 *
 * 🔴 사용자의 실제 매매(매매일지)와 완전히 별개다
 *   두 값을 같은 표에 나란히 놓지 않는다. 저널 채점과 모의투자 결과가
 *   갈리는 것이 정상이고, 그 차이가 곧 타이밍을 놓친 몫이다.
 *
 * 🔴 처음에는 반드시 빈 화면이다
 *   추천이 나온 다음 영업일 장전 08:00(NXT · PaperPreMarketJob)과 장중 09:00:30부터가 첫 매수 기회다(9/28 갱신 — 옛 값 09:05).
 *   ✏️ 장전은 4회차부터다 — 3회차에는 저장 누락으로 한 번도 안 돌았다(2026-09-29 I-57)
 *   왜 비어 있는지를 화면에 적는다 — 안 적으면 배치가 죽은 줄 안다.
 *   (지난 추천 화면에서 같은 것을 겪었다)
 */

/**
 * 탭 하나.
 *
 * @param dividerBefore 이 탭 앞에 구분선을 그린다. (2026-09-10 사용자 요청)
 *   탭이 일곱이 되면서 성격이 다른 것들이 한 줄에 늘어서 있었다.
 *   추천 3계좌 · 저점매수 3계좌(3회차부터 챌린저 합류) · 기록 탭들은 하는 일이 다르다.
 * @param aiMark 라벨 뒤에 🔎(AI 2차 검토 대상)를 붙인다 — 스윙(6개월)만(2026-10-18 · aiReviewMark)
 */
const TABS: { key: PaperTab; label: string; dividerBefore?: boolean; aiMark?: boolean }[] = [
  // 🔴 안전이 먼저다. 하이리스크가 왼쪽에 오면 기본값이 위험한 쪽으로 읽힌다
  { key: 'SAFE', label: '🛡️ 안전' },
  { key: 'MODERATE', label: '⚖️ 분할익절' },
  { key: 'AGGRESSIVE', label: '⚡ 하이리스크' },
  // 저점매수는 매매 방식 자체가 다르다 - 발굴 스윙이 아니라 왕복주 반복 매매 (impl/85)
  // 🔴 라벨이 '밴드'였다가 2026-08-28 사용자 요청으로 바꿨다 - 입문자가 바로 이해하는 말로.
  //   코드·DB 식별자(BAND, td_band_*)는 그대로다. 이름만 바꾸는 마이그레이션은 위험 대비 이득이 없다
  { key: 'BAND', label: '📉 저점매수', dividerBefore: true },
  // 🔴 같은 규칙에 매수 기준가 깊이만 다른 5번째 계좌 (V119, docs/impl/98)
  //   하위 20% → 40%. 감시 대상 50종목 중 매수권이 7개에서 18개로 늘어난다(그때 기준 — 감시는 9/12부터 100종목 ·
  //   9/20부터 익절 +5% · 손절 2.0 ATR 도 다르다)
  { key: 'BAND_AGGRESSIVE', label: '🔥 저점매수(리스크)' },
  // 🔴 챌린저 v1 - 3회차부터 (2026-09-18). 리스크와 견주는 계좌라 바로 옆에 둔다
  //   하위 50% + 종목당 10%를 한 번에(사다리 없음) + 최대 8종목
  { key: 'BAND_CHALLENGER', label: '🧪 저점매수(챌린저)' },
  { key: 'COMPARISON', label: '비교', dividerBefore: true },
  // 🔴 계좌 탭이 아니라 기록을 읽는 탭이다. 그래서 맨 끝이다 (2026-09-03)
  { key: 'ROUNDS', label: '📚 회차 이력' },
];

export function PaperPage() {
  const [tab, setTab] = useState<PaperTab>('COMPARISON');
  // 도움말 모달. 현재 탭의 설계·동작 원리를 입문자 눈높이로 설명한다 (2026-08-28)
  const [helpTab, setHelpTab] = useState<PaperTab | null>(null);
  const ready = useMarketReady();
  const scope = useMarketScope((s) => s.scope);
  /*
    🔴 지금 회차에 쉬는 추천 계좌의 탭은 숨긴다 (2026-10-10 사용자 결정 · 141번 10.6)
      사용자 — "분할익절 · 하이리스크는 기록은 두고 화면에서도 회고도 잠정중단". 4회차부터 둘이 빠진다.
      판정은 서버(매매와 같은 PaperActiveProfiles) — 못 받으면 빈 목록이라 지금처럼 다 보인다.
      지난 회차 성적은 📚 회차 이력 탭에 그대로 남는다(그 회차 판정으로 그린다).
  */
  const restingFiltered = withoutResting(TABS, useRestingProfiles());
  /*
    🔁 국내 스윙 탭 — 4회차부터 저점매수 탭 뒤에 붙는다 (2026-10-10 · 141번 12.6-5)
      서버가 스윙이 참여하는 회차에만 계좌를 준다(3회차까지 빈 목록 → 탭이 안 생긴다). 못 받아도 빈 목록이라 안 생긴다
  */
  const swingTabs = useSwingAccounts().map((s, i) => ({
    key: s.key,
    label: `🔁 ${s.label}`,
    dividerBefore: i === 0,
    aiMark: isAiReviewAccount(s.key),
  }));
  const tabs = insertAfter(restingFiltered, 'BAND_CHALLENGER', swingTabs);

  /*
    🇺🇸 미국주식을 고르면 미국 계좌를 그린다 (2026-09-28, 122번 12.14)
      미국 계좌는 저점매수 p20 하나다(미국 추천 계좌는 I-71 — 붙으면 여기 탭이 늘어난다).
      🔴 isMarketReady('US') 는 그대로 false 다 — 추천 · 지난 추천 같은 다른 미국 화면은 아직 "준비 중" 이 맞다.
        이 화면만 계좌가 생겨 따로 연다. 국내 6계좌 · 비교 · 회차 이력과 섞지 않는다(122번 8.2 "섞이면 둘 다 못 읽는다")
  */
  if (scope === 'US') {
    return <UsPaperView />;
  }

  /*
    🔴 준비 안 된 시장에서는 국내 계좌를 그리지 않고 끝낸다 (2026-09-20 사용자 지적)
      전에는 아래 안내를 배너로 위에만 얹고 국내 6계좌를 그대로 그렸다.
      미국을 고른 채 이 화면을 열면 "준비 중" 과 국내 숫자가 동시에 보였다.
      미국 계좌가 열리면 isMarketReady 만 고치면 이 분기가 저절로 사라진다 (I-64).
   */
  if (!ready) {
    return (
      <div className="space-y-4">
        <TradingNav />
        <MarketNotReady />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <TradingNav />

      <header>
        <h1 className="text-xl font-semibold">AI 모의투자</h1>
        <p className="mt-1 text-sm text-fg-muted">
          여러 매매 방식으로 굴렸으면 어땠는지를 기록합니다. 계좌마다 원금(처음 넣은 돈)은
          각 탭의 계좌 카드에 적혀 있습니다. 실제 주문은 나가지 않습니다. 각 탭의 ? 를 누르면
          그 계좌가 어떻게 사고파는지 설명을 볼 수 있습니다.
        </p>
      </header>

      {/*
        🔴 계좌가 하나 더 붙을 것을 셈해 둔다 (2026-08-30)
          저점매수(리스크) 버전이 들어오면 탭이 6개가 된다. flex-wrap 이라
          폰에서는 두 줄로 접힌다. 그때 줄 사이가 붙지 않게 gap-y 를 주고,
          손가락으로 누를 만큼 높이(py-1.5)를 확보한다.
      */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
        {tabs.map((item) => (
          <Fragment key={item.key}>
            {/*
              그룹 구분선. (2026-09-10 사용자 요청)

              🔴 폰에서 줄이 접히는 것을 셈했다
                flex-wrap 이라 구분선도 아이템처럼 흐른다. 줄 맨 앞에 오면
                허공에 선 하나가 뜨는데, aria-hidden 이고 높이가 글자보다 짧아
                눈에 거슬리지 않는다. 그룹을 div 로 묶으면 wrap 이 그룹 단위로
                일어나 폰에서 세 줄이 되므로 그 편이 더 나쁘다.
            */}
            {item.dividerBefore && (
              <span aria-hidden className="mx-0.5 h-4 w-px shrink-0 self-center bg-line" />
            )}
            <button
              type="button"
              onClick={() => setTab(item.key)}
              className={cx(
                'rounded-full px-3 py-1.5 text-sm transition-colors',
                tab === item.key
                  ? 'bg-brand text-brand-fg'
                  : 'bg-surface-hover text-fg-muted hover:text-fg',
              )}
            >
              {item.label}
              {/* 🔎 AI 2차 검토 대상(스윙(6개월)만) — 화면 읽기 프로그램은 뜻으로 읽는다 */}
              {item.aiMark && (
                <>
                  {' '}
                  <span role="img" aria-label={AI_REVIEW_MARK_LABEL} title={AI_REVIEW_MARK_LABEL}>
                    {AI_REVIEW_MARK}
                  </span>
                </>
              )}
            </button>
          </Fragment>
        ))}

        {/*
          도움말 (2026-08-28 사용자 요청). 지금 보고 있는 탭의 설명을 연다.
          탭마다 ? 를 붙이면 폰에서 줄이 넘친다 - 하나만 두고 현재 탭을 따른다.
        */}
        <button
          type="button"
          onClick={() => setHelpTab(tab)}
          aria-label="이 계좌의 동작 방식 설명"
          className="ml-auto flex h-7 w-7 items-center justify-center rounded-full bg-brand-weak text-sm font-semibold text-brand transition-colors hover:bg-brand hover:text-brand-fg"
        >
          ?
        </button>
      </div>

      {tab === 'ROUNDS' ? (
        <PaperRoundsPanel />
      ) : tab === 'COMPARISON' ? (
        <ComparisonTab />
      ) : tab === 'BAND' ? (
        <BandPanel variant="CONSERVATIVE" />
      ) : tab === 'BAND_AGGRESSIVE' ? (
        <BandPanel variant="AGGRESSIVE" />
      ) : tab === 'BAND_CHALLENGER' ? (
        <BandPanel variant="CHALLENGER" />
      ) : tab === 'SWING_A' || tab === 'SWING_C' || tab === 'SWING_US_A' ? (
        // SWING_US_A 는 국내 탭 줄에 없다(미국 화면 전용) — 여기 올 일은 없지만 빈 프로필 탭을 그리지 않게 막는다
        <SwingPanel account={tab} />
      ) : tab === 'BAND_US_P20' ? (
        // 국내 탭 줄에는 없는 키다(미국 화면의 "?" 전용) — 여기 올 일은 없지만 빈 프로필 탭을 그리지 않게 막는다
        <BandPanel variant="US_P20" />
      ) : (
        <ProfileTab profile={tab} />
      )}

      <ProfileHelpModal tab={helpTab} onClose={() => setHelpTab(null)} />
    </div>
  );
}

/**
 * 🇺🇸 미국 모의투자 화면. (2026-09-28, 122번 12.14)
 *
 * 🇺🇸 미국 추천 R3(4회차부터 · 2026-10-11 E 화면) + 미국 저점매수(동결 p20) + 🔁 미국 스윙(4회차부터 · 서버가 준 탭만 · 2026-10-10).
 * 합계는 원화, 종목 가격은 달러다(BandPanel 이 계좌의 currency 로 가른다).
 */
function UsPaperView() {
  // 🇺🇸 "?" 도움말 (2026-10-10 사용자 — "미국 저점매수는 국내와 다르게 ? 도움말이 안 보인다")
  const [helpOpen, setHelpOpen] = useState(false);
  // 🇺🇸 미국 스윙 탭(2026-10-10 · 145 8.1) — 서버가 준 계좌만(4회차 전엔 빈 목록이라 탭이 안 생긴다 · 국내 스윙 탭과 같은 규칙)
  const swingTabs = useSwingAccounts('US');
  // 🇺🇸 미국 추천 R3(2026-10-11 E 화면) — 국내 화면 순서(추천 → 저점매수 → 스윙)와 맞춰 맨 앞. 계좌 행(V146)이 있어 늘 보이고,
  //   4회차 전엔 "4회차가 열리면 고르기 시작" 안내가 뜬다(PaperOverview.account.waiting)
  type UsTab = 'US_SAFE_R3' | 'BAND_US_P20' | SwingAccountKey;
  const [usTab, setUsTab] = useState<UsTab>('US_SAFE_R3');
  const tabs: { key: UsTab; label: string }[] = [
    { key: 'US_SAFE_R3', label: '🇺🇸 미국 추천' },
    { key: 'BAND_US_P20', label: '🇺🇸 미국 저점매수' },
    ...swingTabs.map((s) => ({ key: s.key, label: `🔁 ${s.label}` })),
  ];
  return (
    <div className="space-y-4">
      <TradingNav />

      <header>
        <h1 className="text-xl font-semibold">AI 모의투자 · 미국주식</h1>
        <p className="mt-1 text-sm text-fg-muted">
          계좌마다 원화 2,000만 원으로 미국 종목을 사고파는 모의 계좌들입니다. 살 때 · 팔 때 그 순간 환율로 바꾸고,
          손익은 원화로 셉니다(환율 몫은 따로 보여 드립니다). 매매는 한국 시각 밤에 미국 정규장에서 일어납니다.
          실제 주문은 나가지 않습니다.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2" role="tablist" aria-label="미국 계좌">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={usTab === t.key}
            onClick={() => setUsTab(t.key)}
            className={cx(
              'rounded-full px-3 py-1.5 text-sm transition-colors',
              usTab === t.key ? 'bg-brand text-brand-fg' : 'bg-surface-hover text-fg-muted hover:text-fg',
            )}
          >
            {t.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          aria-label="이 계좌의 동작 방식 설명"
          className="ml-auto flex h-7 w-7 items-center justify-center rounded-full bg-brand-weak text-sm font-semibold text-brand transition-colors hover:bg-brand hover:text-brand-fg"
        >
          ?
        </button>
      </div>

      {usTab === 'US_SAFE_R3' ? (
        <ProfileTab profile="US_SAFE_R3" />
      ) : usTab === 'BAND_US_P20' ? (
        <BandPanel variant="US_P20" />
      ) : (
        <SwingPanel account={usTab} />
      )}

      <ProfileHelpModal tab={helpOpen ? usTab : null} onClose={() => setHelpOpen(false)} />
    </div>
  );
}

function ComparisonTab() {
  const { data, isLoading, error, refetch } = usePaperComparison();

  if (isLoading) return <Spinner />;
  if (error) return <ErrorState message={error.message} onRetry={() => refetch()} />;
  if (!data) return null;

  return <PaperComparisonPanel data={data} />;
}

function ProfileTab({ profile }: { profile: PaperProfile }) {
  const { data, isLoading, error, refetch } = usePaperOverview(profile);

  if (isLoading) return <Spinner />;
  if (error) return <ErrorState message={error.message} onRetry={() => refetch()} />;
  if (!data) return null;

  // 🇺🇸 행의 가격 · 금액 · 손익 통화 — 미국 추천이면 달러(2026-10-11 E 화면). 카드 숫자는 원화 그대로(백엔드가 환산)
  return (
    <BandPriceFormatContext.Provider value={bandPriceFormat(data.account.currency)}>
      <div className="space-y-4">
        <AccountSummary data={data} />
        <ReturnCurve data={data} />
        <Holdings data={data} />
        <TradeHistory data={data} />
        <ClosedTrades data={data} />
        <Reviews data={data} />
      </div>
    </BandPriceFormatContext.Provider>
  );
}

/**
 * 계좌 카드. 숫자 세 줄은 저점매수와 같은 AccountStatsCard 가 그린다 (2026-09-12).
 * 여기서는 이 계좌에만 있는 각주(시작일·장중/종가 기준·복리)만 붙인다.
 *
 * PaperAccountSummary 는 AccountStats 의 필드를 모두 같은 이름으로 갖고 있어 그대로 넘긴다.
 */
function AccountSummary({ data }: { data: PaperOverview }) {
  const a = data.account;
  return (
    <AccountStatsCard title={`${a.profileLabel} 계좌`} stats={a}>
      <p className="mt-3 text-xs text-fg-subtle">
        이번 회차 {formatDate(a.startedAt)} 시작 · 보유 {a.holdingCount}종목
        {' · '}
        {/* 🔴 장중이면 현재가 기준이라는 것을 밝힌다. 안 밝히면 종가와 달라 보여 오류로 읽힌다 */}
        {a.live ? '장중이라 현재가 기준입니다' : '마지막 종가 기준입니다'}
      </p>
      {/*
        🇺🇸 미국 추천(2026-10-11 E 화면) — 카드는 원화, 아래 표는 달러다. 어느 환율로 바꿨는지 밝힌다.
          환율을 못 받으면 보유를 매수 때 원화 원가로 센다(백엔드 PaperQueryService.usEvalKrw) — 그때는 그렇다고 말한다
      */}
      {a.currency === 'USD' && (
        <p className="mt-1 text-xs text-fg-subtle">
          {a.fxRate !== null
            ? `카드 금액은 원화입니다. 보유 종목의 달러 평가를 환율 ${a.fxRate.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}원으로 바꿔 더했습니다. 아래 표는 달러입니다.`
            : '환율을 아직 못 받아 보유 종목을 산 때의 원화 금액으로 셉니다. 아래 표는 달러입니다.'}
        </p>
      )}
      {a.waiting && (
        <p className="mt-1 text-xs text-badge-warning-fg">
          4회차가 열리면 고르기 시작합니다. 지금은 계좌만 준비돼 있습니다.
        </p>
      )}
      {/*
        자본이 어떻게 굴러가는지를 적는다 (2026-08-23 사용자 질문에서).
        "이익 160만이 가만히 있나?" 는 오해가 실제로 생겼다 - 실현손익은 예수금에
        합산되어 다음 매수의 배분에 들어간다(복리). 보유가 없으면 "대기 중" 이라고 말한다.
      */}
      {/*
        🔴 매수를 쉬는 계좌 (2026-10-08 · I-91). 4회차부터 국내 추천은 안전 계좌만 새로 산다.
        이 안내가 없으면 "왜 안 사지?" 가 장애처럼 보인다. 아래 복리 문장("다음 매수에 재투자")도 쉬는 계좌엔 거짓이라 바꾼다.
      */}
      {a.resting ? (
        <p className="mt-1 text-xs text-badge-warning-fg">
          이번 회차에는 새로 사지 않는 계좌입니다. 들고 있는 종목은 목표가 · 손절가 · 보유 기한(추천일부터
          15영업일)에 닿으면 팔려 정리됩니다.
          {a.holdingCount === 0 && ' 지금은 보유가 모두 정리됐습니다.'}
        </p>
      ) : (
        <p className="mt-1 text-xs text-fg-subtle">
          실현손익은 예수금에 합산되어 다음 매수에 그대로 재투자됩니다 (복리).
          {a.holdingCount === 0 &&
            ' 지금은 보유가 없어 예수금 전액이 다음 추천을 기다리는 중입니다.'}
        </p>
      )}
    </AccountStatsCard>
  );
}

function ReturnCurve({ data }: { data: PaperOverview }) {
  const c = usePaperChartColors();
  // 곡선 색은 프로필의 정체성 색이다. 비교 탭과 같은 색이어야 같은 것으로 읽힌다.
  // 🇺🇸 미국 추천(US_SAFE_R3)은 성격이 "덜 출렁이는 대형주 눌림" 이라 안전 계좌 색을 쓴다(2026-10-11)
  const curveColor =
    data.account.profile === 'SAFE' || data.account.profile === 'US_SAFE_R3'
      ? c.safe
      : data.account.profile === 'MODERATE'
        ? c.moderate
        : c.aggressive;

  /*
    🔴 제목은 "이번 회차 수익률" 이다 (2026-10-19) — 곡선은 이번 회차 시작일부터 회차 시작 금액 대비로 그린다(스냅샷 returnPercent).
      카드의 큰 숫자가 원금 대비 누적으로 바뀌어, 여기를 "누적" 이라 부르면 두 숫자가 같은 이름으로 다르게 나온다
  */
  if (data.curve.length === 0) {
    return (
      <Card title="이번 회차 수익률">
        <EmptyState
          title="아직 그릴 곡선이 없습니다"
          description={
            data.account.currency === 'USD'
              ? '미국 장이 끝난 다음 날 오전 11:00 배치가 하루 한 점씩 남깁니다.'
              : '평일 20:50 배치가 하루 한 점씩 남깁니다.'
          }
        />
      </Card>
    );
  }

  const rows = data.curve.map((point) => ({
    date: point.baseDate.slice(5),
    수익률: point.returnPercent,
  }));

  return (
    <Card title="이번 회차 수익률">
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.line} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: c.axis, fontSize: 11 }} stroke={c.line} />
            <YAxis
              tick={{ fill: c.axis, fontSize: 11 }}
              stroke={c.line}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip
              formatter={(value) => `${Number(value).toFixed(2)}%`}
              contentStyle={{
                background: c.surface,
                border: `1px solid ${c.line}`,
                borderRadius: 8,
                fontSize: 12,
              }}
            />
            <ReferenceLine y={0} stroke={c.goal} strokeDasharray="4 4" />
            {/* 계열이 하나뿐이라 범례를 두지 않는다. 카드 제목이 이름을 대신한다 */}
            <Line
              dataKey="수익률"
              stroke={curveColor}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function Holdings({ data }: { data: PaperOverview }) {
  // 🇺🇸 미국 추천이면 가격 · 금액 · 손익이 달러다(ProfileTab 의 Provider · 2026-10-11 E 화면). 국내는 원화 그대로
  const { px, usd } = useBandPriceFormat();
  const reason = emptyReason(data);
  // 한국 날짜 — UTC 로 자르면 09시 전엔 어제가 되어 만료까지 남은 날이 하루 많게 보였다 (9/28 I-78 2차)
  const today = kstDateString();

  if (data.holdings.length === 0) {
    return (
      <Card title="보유 중">
        <EmptyState
          title="보유 중인 종목이 없습니다"
          description={reason ?? '모두 청산됐습니다. 다음 추천을 기다립니다.'}
        />
      </Card>
    );
  }

  return (
    <Card title={`보유 중 ${data.holdings.length}종목`}>
      <Table>
        <thead>
          <tr>
            <Th>종목</Th>
            <Th align="right">매수가</Th>
            <Th align="right">현재가</Th>
            <Th align="right">평가손익</Th>
            <Th align="right">남은 기간</Th>
          </tr>
        </thead>
        <tbody>
          {data.holdings.map((h) => {
            const left = daysToExpiry(h, today);
            return (
              <tr key={h.tradeId}>
                {/* 🔴 표식은 아래 줄로. 같은 줄에 두면 좁은 폭에서 종목명이 접힌다 */}
                <Td>
                  <div className="flex items-baseline gap-1">
                    <StockLink stockId={h.stockId} country={usd ? 'US' : undefined} className="font-medium">
                      {h.stockName ?? h.symbol}
                    </StockLink>
                    <span className="text-xs text-fg-subtle tabular">{h.quantity}주</span>
                  </div>
                  {/*
                    매수 시각. 없으면(2026-09-10 이전 건) 날짜만 (V126).
                    뒤에 며칠째 들고 있는지를 붙인다 (2026-09-12) - "언제 샀나" 만으로는
                    오늘이 며칠째인지 매번 암산해야 한다. 0일이면 "오늘".
                  */}
                  <span className="block text-xs text-fg-subtle tabular">
                    매수 {h.buyAt ? formatDateTime(h.buyAt) : formatDate(h.buyDate)}
                    {' · '}
                    {daysHeldLabel(h.daysHeld)}
                  </span>
                  {enteredAgainstCondition(h) && (
                    <div className="mt-1">
                      <Badge tone="warning">조건 이탈 매수</Badge>
                    </div>
                  )}
                </Td>
                {/*
                  🔴 단가 아래 둘째 줄에 "수량을 곱한 금액" 을 넣는다 (2026-09-10 사용자 요청)
                    단가와 주수만 있으면 이 종목에 얼마가 들어갔는지 매번 암산해야 한다.
                    폰에서 칸을 늘릴 수 없으니 미래에셋처럼 두 줄로 쌓는다.
                */}
                <Td align="right" numeric>
                  {px(h.buyPrice)}
                  <span className="block text-xs font-normal text-fg-subtle">
                    {px(h.buyPrice * h.quantity)}
                  </span>
                </Td>
                <Td align="right" numeric>
                  {h.currentPrice === null ? (
                    '—'
                  ) : (
                    <>
                      {/* 매수가보다 오르면 빨강, 내리면 파랑 (2026-09-10 사용자 요청) */}
                      <span className={changeColorClass(h.currentPrice - h.buyPrice)}>
                        {px(h.currentPrice)}
                      </span>
                      <span className="block text-xs font-normal text-fg-subtle">
                        {px(h.currentPrice * h.quantity)}
                      </span>
                    </>
                  )}
                </Td>
                <Td align="right">
                  <span className={changeColorClass(h.unrealizedPnl)}>
                    {px(h.unrealizedPnl)}
                  </span>
                  <span className="block text-xs text-fg-subtle">
                    {formatPercent(h.unrealizedPercent)}
                  </span>
                </Td>
                <Td align="right">{left === null ? '—' : `${left}일`}</Td>
              </tr>
            );
          })}

          {/*
            합계 행. (2026-09-10 사용자 요청)

            🔴 현재가를 못 받은 종목은 평가금액에서 빠진다
              그 자리에 매수가를 대신 넣으면 "안 움직였다" 는 거짓 정보가 된다.
              빠진 종목이 있으면 아래 안내 문구가 그것을 말한다.
          */}
          <tr className="border-t-2 border-line font-semibold">
            <Td>합계 {data.holdings.length}종목</Td>
            <Td align="right" numeric>
              {px(data.holdings.reduce((sum, h) => sum + h.buyPrice * h.quantity, 0))}
            </Td>
            <Td align="right" numeric>
              {px(
                data.holdings.reduce(
                  (sum, h) => sum + (h.currentPrice ?? 0) * h.quantity,
                  0,
                ),
              )}
            </Td>
            <Td align="right">
              <span
                className={changeColorClass(
                  data.holdings.reduce((sum, h) => sum + h.unrealizedPnl, 0),
                )}
              >
                {px(data.holdings.reduce((sum, h) => sum + h.unrealizedPnl, 0))}
              </span>
            </Td>
            <Td>{''}</Td>
          </tr>
        </tbody>
      </Table>
      <p className="mt-2 text-xs text-fg-subtle">
        {usd
          ? '이 표는 달러입니다. 평가손익은 매수 수수료를 뺀 달러 값이고, 매도 수수료는 팔 때 반영됩니다(미국은 거래세가 없습니다). 환율 몫까지 넣은 원화 손익은 위 계좌 카드에 있습니다. '
          : '평가손익은 매수 수수료를 뺀 값입니다. 매도 수수료와 거래세는 팔 때 반영됩니다. '}
        단가 아래 작은 숫자는 수량을 곱한 금액입니다.
        {data.holdings.some((h) => h.currentPrice === null) &&
          ' 현재가를 못 받은 종목이 있어 평가금액 합계에서 빠져 있습니다.'}
      </p>
    </Card>
  );
}

/** 청산 목록 한 페이지 분량. 표라서 10건이면 폰 한두 화면이다. */
const CLOSED_PAGE_SIZE = 10;

function ClosedTrades({ data }: { data: PaperOverview }) {
  /*
    클라이언트 페이지네이션 (2026-08-20 사용자 요청 - 이력이 세로로 한없이 길어진다).

    서버 페이징을 하지 않은 이유
      overview 응답은 이미 전체 청산 목록을 담고 있고, 규모가 단일 가족 계정이라
      수년치가 쌓여도 수백 KB 다. 지금 문제는 전송량이 아니라 화면 길이다.
      행이 수천 건이 되면 그때 지난 추천처럼 서버 페이징으로 옮긴다. (impl/71)

    프로필 탭을 바꾸면 data 가 통째로 바뀌는데 컴포넌트는 남는다.
    페이지를 clamp 해서 새 목록보다 큰 페이지에 머무는 것을 막는다.
  */
  const [page, setPage] = useState(0);
  const { px, usd } = useBandPriceFormat();

  if (data.closed.length === 0) {
    return (
      <Card title="청산 완료">
        <EmptyState
          title="아직 청산된 거래가 없습니다"
          description={
            usd
              ? '목표가·손절가에 닿거나 미국 거래일 15일이 지나면 여기에 쌓입니다.'
              : '목표가·손절가에 닿거나 15영업일이 지나면 여기에 쌓입니다.'
          }
        />
      </Card>
    );
  }

  const totalPages = Math.ceil(data.closed.length / CLOSED_PAGE_SIZE);
  const safePage = Math.min(page, totalPages - 1);
  const visible = data.closed.slice(
    safePage * CLOSED_PAGE_SIZE,
    (safePage + 1) * CLOSED_PAGE_SIZE,
  );

  return (
    <Card title={`청산 완료 ${data.closed.length}건`}>
      {/*
        🔴 폰에서 4열은 너무 좁다 (2026-08-30 사용자 피드백)
          "실현 손익" 헤더가 "실현 손 / 익" 으로 끊기고 사유 배지가 뭉쳤다.
          보유일을 종목 칸의 날짜 줄에 합쳐 3열로 줄였다.
          헤더도 "실현 손익" → "손익" 으로 줄여 접힐 일 자체를 없앤다.
          (한글 어절 단위 줄바꿈은 index.css 의 word-break: keep-all 이 맡는다)
      */}
      <Table>
        <thead>
          <tr>
            <Th>종목</Th>
            <Th>사유</Th>
            {/* ? 는 실현 손익이 무엇인지 설명한다 (profitHelp.ts). Th 가 nowrap 이라 버튼이 아랫줄로 안 떨어진다 */}
            <Th align="right">
              <span className="inline-flex items-center">
                손익
                <HelpButton help={PROFIT_HELP} />
              </span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {visible.map((t) => (
            <tr key={t.tradeId}>
              <Td>
                <div className="flex items-baseline gap-1">
                  <StockLink stockId={t.stockId} country={usd ? 'US' : undefined} className="font-medium">
                    {t.stockName ?? t.symbol}
                  </StockLink>
                  <span className="text-xs text-fg-subtle tabular">{t.quantity}주</span>
                </div>
                {/*
                  단가와 금액을 두 줄로 쌓는다. (2026-09-10 사용자 요청)
                  단가만 있으면 이 거래에 얼마가 오갔는지 매번 곱해야 한다.
                  폰에서 열을 늘릴 수 없어(2026-08-30에 4열→3열로 줄인 표다) 세로로 쌓는다.
                */}
                <span className="block text-xs text-fg-subtle tabular">
                  {px(t.buyPrice)} → {px(t.sellPrice)}
                </span>
                <span className="block text-xs text-fg-subtle tabular">
                  {px(t.buyPrice * t.quantity)} → {px(t.sellPrice * t.quantity)}
                </span>
                {/*
                  🔴 체결 시각을 분까지 보여준다 (2026-09-10 사용자 요청, V126)
                    시각이 없는 건은 날짜만 보여준다. 없을 수 있는 이유가 둘이다.
                      ① 2026-09-10 이전 거래 - 그때는 날짜만 저장했다.
                         buy_checkpoint('OPEN')을 09:00 으로 바꿔 채우는 것은 추정이라 하지 않았다
                      ② 🔴 2026-09-21 이전의 회차 종료 청산(2회차 27건) -
                         청산 경로가 sell_at 을 안 채우고 있었다. 같은 날 고쳤으므로
                         3회차 이후 청산은 시각이 있다. 과거 건은 소급하지 않았다 -
                         배치 시각(20:40)으로 추정할 수 있을 뿐 기록이 아니다
                    (이 주석은 2026-09-21 까지 ①만 적고 있었다. 화면 설명이 거짓이었다)
                    두 시각을 한 줄에 두면 폰에서 접히므로 매수·매도를 나눈다.
                */}
                <span className="block text-xs text-fg-subtle tabular">
                  매수 {t.buyAt ? formatDateTime(t.buyAt) : formatDate(t.buyDate)}
                </span>
                <span className="block text-xs text-fg-subtle tabular">
                  매도 {t.sellAt ? formatDateTime(t.sellAt) : formatDate(t.sellDate)} ·{' '}
                  {t.daysHeld}일
                </span>
                {enteredAgainstCondition(t) && (
                  <div className="mt-1">
                    <Badge tone="warning">조건 이탈 매수</Badge>
                  </div>
                )}
              </Td>
              <Td>
                {/* 배지와 "회고 대기" 가 한 줄에 붙으면 좁은 폭에서 뭉친다 */}
                <Badge
                  tone={
                    t.sellReason === 'TARGET'
                      ? 'success'
                      : t.sellReason === 'STOP_LOSS'
                        ? 'danger'
                        : 'neutral'
                  }
                >
                  {t.sellReasonLabel}
                </Badge>
                {/* '회고 대기' 표시는 2026-10-18 거래마다 쓰던 AI 회고를 멈추며 뺐다 — 남기면 영원히 "대기" 로 남는다(reviewPause.ts) */}
              </Td>
              <Td align="right">
                <span className={changeColorClass(t.realizedPnl)}>{px(t.realizedPnl)}</span>
                <span className="block text-xs text-fg-subtle">{formatPercent(t.returnPercent)}</span>
              </Td>
            </tr>
          ))}

          {/*
            합계 행. (2026-09-10 사용자 요청)

            🔴 이 페이지가 아니라 청산 전체의 합이다
              표는 10건씩 끊어 보여주지만 합계까지 페이지별로 내면
              "이 계좌가 얼마를 벌었나" 를 볼 수 없다. 그래서 data.closed 전부를 더한다.
          */}
          <tr className="border-t-2 border-line font-semibold">
            <Td>
              합계 {data.closed.length}건
              <span className="block text-xs font-normal text-fg-subtle tabular">
                {px(data.closed.reduce((sum, t) => sum + t.buyPrice * t.quantity, 0))}
                {' → '}
                {px(data.closed.reduce((sum, t) => sum + t.sellPrice * t.quantity, 0))}
              </span>
            </Td>
            <Td>{''}</Td>
            <Td align="right">
              <span
                className={changeColorClass(
                  data.closed.reduce((sum, t) => sum + t.realizedPnl, 0),
                )}
              >
                {px(data.closed.reduce((sum, t) => sum + t.realizedPnl, 0))}
              </span>
            </Td>
          </tr>
        </tbody>
      </Table>
      <Pager page={safePage} totalPages={totalPages} onChange={setPage} />
      <p className="mt-2 text-xs text-fg-subtle">
        종목 아래 두 줄은 단가와 수량을 곱한 금액입니다. 합계는 이 페이지가 아니라 이번 회차 청산
        전체({data.closed.length}건)를 더한 값입니다.
      </p>
    </Card>
  );
}

/** 회고 한 페이지 분량. 항목마다 여러 문단이라 5건이면 충분히 길다. */
const REVIEW_PAGE_SIZE = 5;

function Reviews({ data }: { data: PaperOverview }) {
  // 페이지네이션 방식과 clamp 이유는 ClosedTrades 주석과 같다
  const [page, setPage] = useState(0);
  const { px } = useBandPriceFormat();

  if (data.reviews.length === 0) {
    return (
      <Card title="회고">
        <EmptyState title="회고가 없습니다" description={REVIEW_PAUSED_NOTE} />
      </Card>
    );
  }

  const totalPages = Math.ceil(data.reviews.length / REVIEW_PAGE_SIZE);
  const safePage = Math.min(page, totalPages - 1);
  const visible = data.reviews.slice(
    safePage * REVIEW_PAGE_SIZE,
    (safePage + 1) * REVIEW_PAGE_SIZE,
  );

  return (
    <Card title={`회고 ${data.reviews.length}건`}>
      <ul className="space-y-3">
        {visible.map((r) => (
          <li key={r.reviewId} className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-sm font-medium">{r.stockName ?? r.symbol}</span>
              {r.sellDate && <span className="text-xs text-fg-subtle">{formatDate(r.sellDate)}</span>}
              {r.realizedPnl !== null && (
                <span className={cx('text-xs tabular', changeColorClass(r.realizedPnl))}>
                  {px(r.realizedPnl)}
                </span>
              )}
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-fg-muted">{toThousands(r.content)}</p>
          </li>
        ))}
      </ul>
      <Pager page={safePage} totalPages={totalPages} onChange={setPage} />
      <p className="mt-3 text-xs text-fg-subtle">{REVIEW_PAUSED_NOTE} 위 목록은 그 전에 쓰인 회고입니다.</p>
    </Card>
  );
}

// ─────────────────────────────────────────────
// 매매 이력 (2026-08-23 사용자 요청)
// ─────────────────────────────────────────────

/** 이력 한 줄. 매수와 매도를 같은 모양으로 두어 시간순으로 섞어 읽는다. */
interface TradeEvent {
  key: string;
  date: string;
  /** 체결 시각. 2026-09-10 이전 건은 null 이라 화면이 날짜만 그린다 (V126) */
  at: string | null;
  side: 'BUY' | 'SELL';
  /** 종목 팝업용 (I-60) */
  stockId: number;
  name: string;
  price: number;
  quantity: number;
  /** 매수면 체크포인트, 매도면 사유. 장중 매도는 사유 뒤에 "장중" 을 붙인다 */
  note: string;
  pnl: number | null;
}

const HISTORY_PAGE_SIZE = 10;

/**
 * 매수·매도를 한 줄기로 최신순 정렬한 이력.
 *
 * 왜 따로 두나 - "청산 완료" 는 매도 시점 기준이라 "언제 뭘 샀는지" 의 흐름이
 * 안 보인다. 사용자가 원한 것은 통장 거래내역처럼 시간순으로 읽히는 것이다.
 *
 * 🔴 백엔드를 바꾸지 않았다
 *   overview 가 이미 보유(매수 정보)와 청산(매수+매도 정보)을 다 준다.
 *   여기서 이벤트로 풀어 정렬만 한다. 같은 날 매수·매도가 있으면 매도가 위다 -
 *   하루 안에서는 산 뒤에 팔았을 것이기 때문이다.
 */
function TradeHistory({ data }: { data: PaperOverview }) {
  const [page, setPage] = useState(0);
  const { px, usd } = useBandPriceFormat();

  const events: TradeEvent[] = [
    ...data.holdings.map((h) => ({
      key: `buy-${h.tradeId}`,
      date: h.buyDate,
      at: h.buyAt,
      side: 'BUY' as const,
      stockId: h.stockId,
      name: h.stockName ?? h.symbol ?? '-',
      price: h.buyPrice,
      quantity: h.quantity,
      // 장전(NXT) 매수도 체크포인트가 null 이라 시각으로 가른다 (I-3)
      note: isPreMarketBuy(h.buyAt)
        ? '장전 · NXT'
        : h.buyCheckpoint === null
          ? '장중 · 눌림'
          : `${h.buyCheckpoint} 체크포인트`,
      pnl: null,
    })),
    ...data.closed.flatMap((t) => [
      {
        key: `buy-${t.tradeId}`,
        date: t.buyDate,
        at: t.buyAt,
        side: 'BUY' as const,
        stockId: t.stockId,
        name: t.stockName ?? t.symbol ?? '-',
        price: t.buyPrice,
        quantity: t.quantity,
        note: isPreMarketBuy(t.buyAt) ? '매수 · 장전 NXT' : t.buyCheckpoint === null ? '매수 · 장중' : '매수',
        pnl: null,
      },
      {
        key: `sell-${t.tradeId}`,
        date: t.sellDate,
        at: t.sellAt,
        side: 'SELL' as const,
        stockId: t.stockId,
        name: t.stockName ?? t.symbol ?? '-',
        price: t.sellPrice,
        quantity: t.quantity,
        note: t.sellCheckpoint === null ? `${t.sellReasonLabel} · 장중` : t.sellReasonLabel,
        pnl: t.realizedPnl,
      },
    ]),
  ].sort((a, b) =>
    a.date === b.date
      ? (a.side === 'SELL' ? -1 : 0) - (b.side === 'SELL' ? -1 : 0)
      : b.date.localeCompare(a.date),
  );

  if (events.length === 0) {
    return (
      <Card title="매매 이력">
        <EmptyState
          title="아직 매매가 없습니다"
          description="첫 매수가 일어나면 여기에 시간순으로 쌓입니다."
        />
      </Card>
    );
  }

  const totalPages = Math.ceil(events.length / HISTORY_PAGE_SIZE);
  const safePage = Math.min(page, totalPages - 1);
  const visible = events.slice(safePage * HISTORY_PAGE_SIZE, (safePage + 1) * HISTORY_PAGE_SIZE);

  return (
    <Card title={`매매 이력 ${events.length}건`}>
      <Table>
        <thead>
          <tr>
            <Th>일시</Th>
            <Th>구분</Th>
            <Th>종목</Th>
            <Th align="right">단가 × 수량</Th>
            {/* ? 는 실현 손익이 무엇인지 설명한다 (profitHelp.ts). Th 가 nowrap 이라 버튼이 아랫줄로 안 떨어진다 */}
            <Th align="right">
              <span className="inline-flex items-center">
                실현 손익
                <HelpButton help={PROFIT_HELP} />
              </span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {visible.map((e) => (
            <tr key={e.key}>
              {/*
                체결 시각을 분까지. (2026-09-10 사용자 요청, V126)
                2026-09-10 이전 건은 시각을 저장한 적이 없어 날짜만 나온다 -
                'OPEN' 을 09:00 으로 바꿔 채우면 기록이 아니라 추측이 된다.
              */}
              <Td>{e.at ? formatDateTime(e.at) : formatDate(e.date)}</Td>
              <Td>
                <Badge tone={e.side === 'BUY' ? 'info' : 'neutral'}>
                  {e.side === 'BUY' ? '매수' : '매도'}
                </Badge>
                <span className="ml-1 text-xs text-fg-subtle">{e.note}</span>
              </Td>
              <Td>
                <StockLink stockId={e.stockId} country={usd ? 'US' : undefined}>{e.name}</StockLink>
              </Td>
              {/* 단가 아래 둘째 줄에 곱한 금액 (2026-09-10 사용자 요청) */}
              <Td align="right" numeric>
                {px(e.price)} × {e.quantity}
                <span className="block text-xs font-normal text-fg-subtle">
                  {px(e.price * e.quantity)}
                </span>
              </Td>
              <Td align="right">
                {e.pnl === null ? (
                  <span className="text-fg-subtle">-</span>
                ) : (
                  <span className={changeColorClass(e.pnl)}>{px(e.pnl)}</span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pager page={safePage} totalPages={totalPages} onChange={setPage} />
    </Card>
  );
}
